import { PermissionType } from '@/enums/PermissionType';

/**
 * What the current role may do on the Menú page (plan §5.17). Every member
 * may view it; without catalog permissions it is read-only.
 */
export interface ICatalogAccess {
  canManage: boolean;
  /** Turn products and categories on and off. */
  canToggle: boolean;
  /** Hide or show in every branch at once. */
  canHideEverywhere: boolean;
  canSchedule: boolean;
  canTranslate: boolean;
  /** See the translations (read only without `TranslationsManage`). */
  readTranslations: boolean;
  canStock: boolean;
  canBulkPrice: boolean;
  canDiscountsSwitch: boolean;
  /** The storefront's «Lo más pedido» switch (Storefront V2 D-ADM-4). */
  canPopularSwitch: boolean;
  showMenuSwitcher: boolean;
  /** Sticker labels (tier 2): «Etiqueta adhesiva» links to them. */
  showLabels: boolean;
  /** Plan-gated capabilities the role has but the plan lacks: shown with the upsell. */
  upsellSchedule: boolean;
  upsellTranslate: boolean;
  upsellStock: boolean;
  upsellBulkPrice: boolean;
}

export interface ICatalogAccessInput {
  has: (permission: PermissionType) => boolean;
  /** The store's effective plan tier. */
  tier: number;
  multipleMenus: boolean;
  /** Staff, owners and roles with `allBranches` (the rule `hasRole` applies). */
  allBranches: boolean;
}

export function catalogAccess(input: ICatalogAccessInput): ICatalogAccess {
  const manage = input.has(PermissionType.CatalogManage);
  const disable = input.has(PermissionType.CatalogDisable);
  const settings = input.has(PermissionType.StoreSettings);
  const translations = input.has(PermissionType.TranslationsManage);
  const paid = input.tier >= 1;
  const canToggle = manage || disable;

  return {
    canManage: manage,
    canToggle,
    canHideEverywhere: canToggle && input.allBranches,
    canSchedule: manage && paid,
    canTranslate: translations && paid,
    readTranslations: paid,
    canStock: manage && paid,
    canBulkPrice: manage && paid,
    canDiscountsSwitch: settings,
    canPopularSwitch: settings,
    showMenuSwitcher: input.multipleMenus,
    showLabels: input.tier >= 2,
    upsellSchedule: manage && !paid,
    upsellTranslate: translations && !paid,
    upsellStock: manage && !paid,
    upsellBulkPrice: manage && !paid,
  };
}
